<template>
  <div
    ref="swipeCellRef"
    class="c-swipe-cell"
    @touchstart.passive="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend="onTouchEnd"
    @click="onClick('cell')"
  >
    <div
      class="c-swipe-cell__wrapper"
      :style="{
        transform: `translateX(${state.offset}px)`,
        transitionDuration: state.dragging ? '0s' : '.6s',
      }"
    >
      <slot />
      <div
        ref="rightRef"
        class="c-swipe-cell__right"
      >
        <slot name="right" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useClickAway } from './useClickAway'
import { useTouch } from './useTouch'

const emit = defineEmits(['click'])

const root = useTemplateRef('swipeCellRef')
const rightRef = useTemplateRef('rightRef')

let opened = false
let lockClick = false
let startOffset = 0

const state = reactive({
  offset: 0,
  dragging: false
})

const touch = useTouch()

const rightWidth = computed(() => rightRef.value?.getBoundingClientRect().width ?? 0)

const open = () => {
  state.offset = -1 * rightWidth.value

  if (!opened) {
    opened = true
  }
}

const close = () => {
  state.offset = 0

  if (opened) {
    opened = false
  }
}

const toggle = () => {
  const offset = Math.abs(state.offset)
  const THRESHOLD = 0.15
  const threshold = opened ? 1 - THRESHOLD : THRESHOLD
  const width = rightWidth.value

  if (width && offset > width * threshold) {
    open()
  } else {
    close()
  }
}

const clamp = (num, min, max) =>
  Math.min(Math.max(num, min), max)

const onTouchStart = (event) => {
  startOffset = state.offset
  touch.start(event)
}

const onTouchMove = (event) => {
  const { deltaX } = touch
  touch.move(event)

  if (touch.isHorizontal()) {
    lockClick = true
    state.dragging = true

    const isEdge = !opened || deltaX.value * startOffset < 0
    if (isEdge) {
      event.stopPropagation()
    }

    state.offset = clamp(
      deltaX.value + startOffset,
      -rightWidth.value,
      0
    )
  }
}

const onTouchEnd = () => {
  if (state.dragging) {
    state.dragging = false
    toggle(state.offset > 0 ? 'left' : 'right')

    // compatible with desktop scenario
    setTimeout(() => {
      lockClick = false
    }, 0)
  }
}

const onClick = (position) => {
  if (lockClick) return

  if (position === 'cell') {
    emit('click', {
      opened
    })
  }

  close()
}

useClickAway(root, (event) => {
  onClick('outside')
}, {
  eventName: 'touchstart'
})
</script>

<style lang="less">
.c-swipe-cell {
  position: relative;
  overflow: hidden;
  cursor: grab;

  &__wrapper {
    transition-timing-function: cubic-bezier(0.18, 0.89, 0.32, 1);
    transition-property: transform;
    user-select: none;
  }

  &__right {
    position: absolute;
    top: 0;
    height: 100%;
    background: #f5f5f5;
  }

  &__right {
    right: 0;
    transform: translate3d(100%, 0, 0);
  }
}
</style>
